"use client";

import { createContext, useContext, useSyncExternalStore } from "react";
import type { DocsLanguage } from "src/lib/DocsLanguage";
import type { DocsUiLibrary } from "src/lib/DocsUiLibrary";

/**
 * Each docs choice (the SDK language, the UI library) as an external store: the context value never
 * changes, and each example subscribes to the choice itself. A changing context value would reach
 * the API reference's endpoint cards while they wait to hydrate (DeferredHydration), which React
 * would then render afresh instead of hydrating; examples that hydrate after a change start from the
 * choice the server rendered (the server snapshot) and then switch.
 */
export interface DocsChoiceStore<T extends string> {
  /** The choice the server rendered with. */
  readonly initial: T;
  get: () => T;
  set: (value: T) => void;
  subscribe: (listener: () => void) => () => void;
  select: (value: T, anchor?: HTMLElement | null) => void;
}

export interface DocsChoices {
  language: DocsChoiceStore<DocsLanguage>;
  uiLibrary: DocsChoiceStore<DocsUiLibrary>;
}

export const DocsLanguageContext = createContext<DocsChoices | null>(null);

const useDocsChoices = () => {
  const choices = useContext(DocsLanguageContext);
  if (!choices) throw new Error("Documentation examples require DocsLanguageProvider");
  return choices;
};

/** The selected language, re-rendering on changes, and the function that selects another. */
export const useDocsLanguage = () => {
  const store = useDocsChoices().language;
  const language = useSyncExternalStore(store.subscribe, store.get, () => store.initial);
  return { language, selectLanguage: store.select };
};

/** Reads the selected language when called, without re-rendering when it changes. */
export const useDocsLanguageReader = () => useDocsChoices().language.get;

/** The selected UI library, re-rendering on changes, and the function that selects another. */
export const useDocsUiLibrary = () => {
  const store = useDocsChoices().uiLibrary;
  const uiLibrary = useSyncExternalStore(store.subscribe, store.get, () => store.initial);
  return { uiLibrary, selectUiLibrary: store.select };
};
